import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { logError } from '../utils/log';

export const PlayoffBuilderPage = ({ userRole }) => {
    const [standings, setStandings] = useState([]);
    const [schedules, setSchedules] = useState([]);
    const [loading, setLoading] = useState(true);

    const [playoffSize, setPlayoffSize] = useState(0);
    const [playoffTeams, setPlayoffTeams] = useState([]);
    const [playoffSlots, setPlayoffSlots] = useState([]);
    const [playoffBracket, setPlayoffBracket] = useState(null);

    useEffect(() => {
        if (userRole !== 'admin') {
            setLoading(false);
            return;
        }
        const calculateStandings = async () => {
            setLoading(true);
            try {
                const teamsSnapshot = await getDocs(collection(db, "teams"));
                const teamsData = teamsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                const schedulesSnapshot = await getDocs(collection(db, "schedules"));
                const allSchedules = schedulesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setSchedules(allSchedules);

                const standingsData = {};
                teamsData.forEach(team => {
                    standingsData[team.id] = { id: team.id, name: team.name, gamePoints: 0, matchWins: 0, matchLosses: 0 };
                });

                const sortedSchedules = allSchedules.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                const currentSchedule = sortedSchedules.length > 0 ? sortedSchedules[0] : null;

                if (currentSchedule) {
                    currentSchedule.weeks.forEach(week => {
                        week.games.forEach(match => {
                            if (match.results) {
                                const homeTeamId = match.homeTeamId;
                                const awayTeamId = match.awayTeamId;
                                const homePoints = match.results.matchStats.home.gamePoints;
                                const awayPoints = match.results.matchStats.away.gamePoints;

                                if (standingsData[homeTeamId]) {
                                    standingsData[homeTeamId].gamePoints += homePoints;
                                    if (homePoints > awayPoints) standingsData[homeTeamId].matchWins++; else standingsData[homeTeamId].matchLosses++;
                                }
                                if (standingsData[awayTeamId]) {
                                    standingsData[awayTeamId].gamePoints += awayPoints;
                                    if (awayPoints > homePoints) standingsData[awayTeamId].matchWins++; else standingsData[awayTeamId].matchLosses++;
                                }
                            }
                        });
                    });
                }

                const standingsArray = Object.values(standingsData);
                setStandings(standingsArray);

            } catch (error) {
                logError("Error calculating standings: ", error);
            }
            setLoading(false);
        };

        calculateStandings();
    }, [userRole]);

    const sortedStandings = useMemo(() => {
        let sortableItems = [...standings];
        const sortedSchedules = [...schedules].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        const currentSchedule = sortedSchedules.length > 0 ? sortedSchedules[0] : null;

        sortableItems.sort((a, b) => {
            if (b.gamePoints !== a.gamePoints) {
                return b.gamePoints - a.gamePoints;
            }

            // Head-to-head tiebreaker for gamePoints
            if (currentSchedule && currentSchedule.weeks) {
                for (const week of currentSchedule.weeks) {
                    for (const game of week.games) {
                        const isMatch = (game.homeTeamId === a.id && game.awayTeamId === b.id) || (game.homeTeamId === b.id && game.awayTeamId === a.id);
                        if (isMatch && game.results && game.results.matchStats) {
                            const homePoints = game.results.matchStats.home.gamePoints;
                            const awayPoints = game.results.matchStats.away.gamePoints;
                            if (homePoints !== awayPoints) {
                                const winnerId = homePoints > awayPoints ? game.homeTeamId : game.awayTeamId;
                                if (winnerId === a.id) return -1; // a wins, ranked higher
                                if (winnerId === b.id) return 1;  // b wins, ranked higher
                            }
                        }
                    }
                }
            }

            // If still tied, use matchWins as secondary tiebreaker
            return b.matchWins - a.matchWins;
        });
        return sortableItems.map((team, index) => ({ ...team, rank: index + 1 }));
    }, [standings, schedules]);

    const handleSetPlayoffSize = (size) => {
        setPlayoffSize(size);
        const topTeams = sortedStandings.slice(0, size);
        setPlayoffTeams(topTeams);
        setPlayoffSlots(topTeams.map((team, index) => ({ seed: index + 1, teamId: team.id })));
    };

    const handleGenerateBracket = () => {
        const bracket = { round1: [], round2: [], finals: [] };
        const getTeam = (seed) => playoffSlots.find(s => s.seed === seed);
    
        if (playoffSize === 8) {
            // Round 1
            bracket.round1.push({ home: getTeam(1), away: getTeam(8), id: 'R1M1' });
            bracket.round1.push({ home: getTeam(4), away: getTeam(5), id: 'R1M2' });
            bracket.round1.push({ home: getTeam(2), away: getTeam(7), id: 'R1M3' });
            bracket.round1.push({ home: getTeam(3), away: getTeam(6), id: 'R1M4' });
            // Round 2 (Semi-Finals)
            bracket.round2.push({ home: { advancement: { from: 'R1M1', team: 'winner' } }, away: { advancement: { from: 'R1M2', team: 'winner' } }, id: 'R2M1' });
            bracket.round2.push({ home: { advancement: { from: 'R1M3', team: 'winner' } }, away: { advancement: { from: 'R1M4', team: 'winner' } }, id: 'R2M2' });
            // Finals
            bracket.finals.push({ home: { advancement: { from: 'R2M1', team: 'winner' } }, away: { advancement: { from: 'R2M2', team: 'winner' } }, id: 'FINALS' });
        } else if (playoffSize === 6) {
            // Round 1 (seeds 1 & 2 have byes)
            bracket.round1.push({ home: getTeam(3), away: getTeam(6), id: 'R1M1' });
            bracket.round1.push({ home: getTeam(4), away: getTeam(5), id: 'R1M2' });
            // Round 2 (Semi-Finals)
            bracket.round2.push({ home: getTeam(1), away: { advancement: { from: 'R1M1', team: 'winner' } }, id: 'R2M1' });
            bracket.round2.push({ home: getTeam(2), away: { advancement: { from: 'R1M2', team: 'winner' } }, id: 'R2M2' });
            // Finals
            bracket.finals.push({ home: { advancement: { from: 'R2M1', team: 'winner' } }, away: { advancement: { from: 'R2M2', team: 'winner' } }, id: 'FINALS' });
        } else if (playoffSize === 4) {
            // Round 1 (Semi-Finals)
            bracket.round1.push({ home: getTeam(1), away: getTeam(4), id: 'R1M1' });
            bracket.round1.push({ home: getTeam(2), away: getTeam(3), id: 'R1M2' });
            // Finals
            bracket.finals.push({ home: { advancement: { from: 'R1M1', team: 'winner' } }, away: { advancement: { from: 'R1M2', team: 'winner' } }, id: 'FINALS' });
        }
        setPlayoffBracket(bracket);
    };

    const handleFinalizePlayoffs = async () => {
        if (!playoffBracket) return;
        if (!window.confirm("Are you sure you want to finalize and save this playoff bracket to the live schedule?")) return;

        const sortedSchedules = schedules.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        const currentSchedule = sortedSchedules.length > 0 ? sortedSchedules[0] : null;
        if (!currentSchedule) {
            alert("No active schedule found to update.");
            return;
        }

        const updatedWeeks = JSON.parse(JSON.stringify(currentSchedule.weeks));
        
        // Helper to create a game object
        const createGame = (match) => ({
            homeTeamId: match.home.teamId || 'TBD',
            awayTeamId: match.away.teamId || 'TBD',
            venue: match.home.teamId ? (sortedStandings.find(t => t.id === match.home.teamId)?.homeLocation || 'TBD') : 'TBD',
            advancement: {
                home: match.home.advancement || null,
                away: match.away.advancement || null,
            },
            playoffId: match.id,
        });

        // Find playoff weeks and populate them
        const playoffWeek1 = updatedWeeks.find(w => w.week === 'Playoffs Wk 1');
        const playoffWeek2 = updatedWeeks.find(w => w.week === 'Playoffs Wk 2');
        const championshipWeek = updatedWeeks.find(w => w.week === 'Championship');

        if (playoffWeek1) {
            playoffWeek1.games = playoffBracket.round1.map(createGame);
        }
        if (playoffWeek2) {
            playoffWeek2.games = playoffBracket.round2.map(createGame);
        }
        if (championshipWeek) {
            championshipWeek.games = playoffBracket.finals.map(createGame);
        }

        const scheduleDocRef = doc(db, "schedules", currentSchedule.id);
        await updateDoc(scheduleDocRef, { 
            weeks: updatedWeeks,
            // Store the bracket definition for later reference
            playoffBracket: playoffBracket 
        });
        alert("Playoff schedule has been updated successfully!");
    };

    const handleSlotChange = (seed, newTeamId) => {
        setPlayoffSlots(currentSlots => {
            const newSlots = [...currentSlots];
            const targetSlot = newSlots.find(s => s.seed === seed);
            if (targetSlot) {
                targetSlot.teamId = newTeamId;
            }
            return newSlots;
        });
    };

    if (userRole !== 'admin') {
        return <p className="text-xl text-danger text-center">Access Denied. Only administrators can view this page.</p>;
    }

    if (loading) return <div className="text-center">Loading league data...</div>;

    return (
        <div className="space-y-8">
            <h2 className="page-title heading-primary-1 text-4xl font-extrabold text-center">Playoff Builder</h2>

            {/* Step 1: Final Standings */}
            <div className="card p-6 rounded-lg shadow-md">
                <h3 className="heading-primary-1 text-2xl font-bold mb-4">1. Final Regular Season Standings</h3>
                <div className="overflow-x-auto">
                    <table className="min-w-max sm:min-w-full divide-y">
                        <thead className="table-header">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase">Rank</th>
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase">Team</th>
                                <th className="px-4 py-3 text-center text-xs font-medium uppercase">W-L</th>
                                <th className="px-4 py-3 text-center text-xs font-medium uppercase">Game Points</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedStandings.map((team, index) => (
                                <tr key={team.id} className={index % 2 === 1 ? 'row-alt' : ''}>
                                    <td className="px-4 py-3 font-bold border-b border-low">{team.rank}</td>
                                    <td className="px-4 py-3 font-semibold border-b border-low">{team.name}</td>
                                    <td className="px-4 py-3 text-center border-b border-low">{team.matchWins}-{team.matchLosses}</td>
                                    <td className="px-4 py-3 text-center font-bold subheading-primary-1 border-b border-low">{team.gamePoints}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Step 2: Select Playoff Size */}
            <div className="card p-6 rounded-lg shadow-md">
                <h3 className="heading-primary-1 text-2xl font-bold mb-4">2. Select Playoff Size</h3>
                <div className="flex justify-center gap-4">
                    {[4, 6, 8].map(size => (
                        <button key={size} onClick={() => handleSetPlayoffSize(size)} className={`segmented-btn font-bold py-2 px-6 rounded-lg transition ${playoffSize === size ? 'segmented-btn-active' : ''}`}>
                            {size} Teams
                        </button>
                    ))}
                </div>
                {playoffSize > 0 && (
                    <div className="mt-6 text-center">
                        <button onClick={handleGenerateBracket} className="btn btn-primary font-bold py-3 px-6 rounded-lg shadow-lg">
                            Generate Bracket
                        </button>
                    </div>
                )}
            </div>

            {/* Step 3: Set Seeds */}
            {playoffBracket && (
                <div className="card p-6 rounded-lg shadow-md">
                    <h3 className="heading-primary-1 text-2xl font-bold mb-4">3. Set Playoff Seeds</h3>
                    <p className="text-muted mb-4">The top {playoffSize} teams have been automatically seeded. You can manually adjust the seeds if needed.</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {playoffSlots.map(({ seed, teamId }) => (
                            <div key={seed} className="p-4 surface-low border border-low rounded-lg">
                                <label className="block text-sm font-bold text-muted mb-1">Seed #{seed}</label>
                                <select
                                    value={teamId}
                                    onChange={(e) => handleSlotChange(seed, e.target.value)}
                                    className="p-2 border border-low rounded w-full surface-low"
                                >
                                    {playoffTeams.map(team => (
                                        <option key={team.id} value={team.id}>{team.name}</option>
                                    ))}
                                </select>
                            </div>
                        ))}
                    </div>
                    {/* Display Generated Bracket */}
                    <div className="mt-6 border-t border-low pt-4">
                        <h4 className="text-xl font-semibold text-strong mb-2">Generated Round 1 Matches</h4>
                        <div className="space-y-2">
                            {playoffBracket.round1.map((match, index) => (
                                <div key={index} className="p-3 surface-low border border-low rounded-md text-center">
                                    <span className="font-bold">{sortedStandings.find(t => t.id === match.away.teamId)?.name || match.away.teamId}</span>
                                    <span className="mx-2">at</span>
                                    <span className="font-bold">{sortedStandings.find(t => t.id === match.home.teamId)?.name || match.home.teamId}</span>
                                </div>
                            ))}
                            {playoffSize === 6 && <p className="text-center text-muted italic">Seeds 1 and 2 have a first-round bye.</p>}
                        </div>
                    </div>
                    <div className="mt-6 text-center">
                        <button onClick={handleFinalizePlayoffs} className="btn btn-success font-bold py-3 px-6 rounded-lg shadow-lg">
                            Finalize & Add to Schedule
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};